import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiTextBLight,
  PiTextItalicLight,
  PiTextUnderlineLight,
  PiListBulletsLight,
  PiListNumbersLight,
  PiCaretDownLight
} from 'react-icons/pi';
import { Menu, Transition } from '@headlessui/react';

interface EditorToolbarProps {
  editorRef: React.RefObject<HTMLDivElement | null>;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isUnorderedList: boolean;
  isOrderedList: boolean;
  currentBlockType: string;
  onFormatChange: () => void;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editorRef,
  isBold,
  isItalic,
  isUnderline,
  isUnorderedList,
  isOrderedList,
  currentBlockType,
  onFormatChange
}) => {
  const { t } = useTranslation();

  const applyFormat = useCallback((command: string) => {
    if (editorRef.current) {
      const savedRange = saveSelection();
      editorRef.current.focus();
      document.execCommand(command, false, undefined);
      restoreSelection(savedRange);
      onFormatChange();
    }
  }, [editorRef, onFormatChange]);

  const handleHeading = useCallback((level: number | 'p') => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const blockElement = getBlockAncestor(range.startContainer, editorRef.current);
    if (!blockElement) return;

    const currentTag = blockElement.tagName.toLowerCase();
    const newTag = level === 'p' ? 'p' : `h${level}`;
    if (currentTag === newTag) return;

    const newElement = document.createElement(newTag);
    newElement.innerHTML = blockElement.innerHTML;
    if (blockElement.id) newElement.id = blockElement.id;
    blockElement.parentNode?.replaceChild(newElement, blockElement);

    const newRange = document.createRange();
    newRange.selectNodeContents(newElement);
    newRange.collapse(false);
    selection.removeAllRanges();
    selection.addRange(newRange);
    onFormatChange();
  }, [editorRef, onFormatChange]);

  const saveSelection = (): Range | null => {
    const selection = window.getSelection();
    return selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
  };

  const restoreSelection = (range: Range | null) => {
    if (range) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const getBlockAncestor = (node: Node, editorRef: HTMLDivElement | null): HTMLElement | null => {
    let current: Node | null = node;
    while (current && editorRef && current !== editorRef) {
      if (current.nodeType === 1) {
        const tag = (current as HTMLElement).tagName.toLowerCase();
        if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return current as HTMLElement;
      }
      current = current.parentNode;
    }
    return null;
  };

  const headingLabel = currentBlockType.startsWith('Header')
    ? `H${currentBlockType.split(' ')[1]}`
    : t('Heading');

  return (
    <div className="glass-bar flex-shrink-0 border-b border-hairline px-3 py-2 flex items-center gap-1 sticky top-0 z-30 shadow-sm">
      {/* Block type selector */}
      <Menu as="div" className="relative inline-block text-start">
        <Menu.Button
          className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded border transition-colors ${
            currentBlockType !== 'Normal text'
              ? 'bg-surface-soft text-champagne border-champagne/50'
              : 'bg-surface-sunken/70 text-fg-body border-hairline hover:border-champagne/60'
          }`}
        >
          {currentBlockType === 'Normal text' ? t('Heading') : headingLabel}
          <PiCaretDownLight className="w-3 h-3" />
        </Menu.Button>
        <Transition
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="glass absolute start-0 z-10 w-40 mt-2 origin-top-left rounded-lg shadow-lg">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => handleHeading('p')}
                    className={`block w-full text-start px-4 py-2 text-sm ${
                      active ? 'bg-surface-sunken/70' : ''
                    } ${currentBlockType === 'Normal text' ? 'bg-surface-soft text-fg-strong' : 'text-fg-body'}`}
                  >
                    {t('Normal text')}
                  </button>
                )}
              </Menu.Item>
              {[1, 2, 3, 4, 5, 6].map((level) => (
                <Menu.Item key={level}>
                  {({ active }) => (
                    <button
                      onClick={() => handleHeading(level)}
                      className={`block w-full text-start px-4 py-2 text-sm ${
                        active ? 'bg-surface-sunken/70' : ''
                      } ${currentBlockType === `Header ${level}` ? 'bg-surface-soft text-fg-strong' : 'text-fg-body'}`}
                      style={{ fontSize: `${24 - level * 2}px` }}
                    >
                      {t('Heading')} {level}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      {/* Divider */}
      <div className="w-px h-6 bg-hairline mx-1" />

      {/* Bold button */}
      <button
        onClick={() => applyFormat('bold')}
        className={`p-1.5 rounded transition-colors ${
          isBold
            ? 'bg-surface-soft text-champagne ring-1 ring-champagne/40'
            : 'text-fg-body hover:bg-surface-sunken/70'
        }`}
        title={t('Bold (Ctrl+B)')}
      >
        <PiTextBLight className="w-4 h-4" />
      </button>

      {/* Italic button */}
      <button
        onClick={() => applyFormat('italic')}
        className={`p-1.5 rounded transition-colors ${
          isItalic
            ? 'bg-surface-soft text-champagne ring-1 ring-champagne/40'
            : 'text-fg-body hover:bg-surface-sunken/70'
        }`}
        title={t('Italic (Ctrl+I)')}
      >
        <PiTextItalicLight className="w-4 h-4" />
      </button>

      {/* Underline button */}
      <button
        onClick={() => applyFormat('underline')}
        className={`p-1.5 rounded transition-colors ${
          isUnderline
            ? 'bg-surface-soft text-champagne ring-1 ring-champagne/40'
            : 'text-fg-body hover:bg-surface-sunken/70'
        }`}
        title={t('Underline (Ctrl+U)')}
      >
        <PiTextUnderlineLight className="w-4 h-4" />
      </button>

      {/* Divider */}
      <div className="w-px h-6 bg-hairline mx-1" />

      {/* Unordered list button */}
      <button
        onClick={() => applyFormat('insertUnorderedList')}
        className={`p-1.5 rounded transition-colors ${
          isUnorderedList
            ? 'bg-surface-soft text-champagne ring-1 ring-champagne/40'
            : 'text-fg-body hover:bg-surface-sunken/70'
        }`}
        title={t('Bullet list')}
      >
        <PiListBulletsLight className="w-4 h-4" />
      </button>

      {/* Ordered list button */}
      <button
        onClick={() => applyFormat('insertOrderedList')}
        className={`p-1.5 rounded transition-colors ${
          isOrderedList
            ? 'bg-surface-soft text-champagne ring-1 ring-champagne/40'
            : 'text-fg-body hover:bg-surface-sunken/70'
        }`}
        title={t('Numbered list')}
      >
        <PiListNumbersLight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EditorToolbar;
